import React, { forwardRef, useImperativeHandle, useReducer } from 'react';

import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import { removeTime } from 'lane-shared/domains/visitorManagement/helpers';
import {
  createScheduleReducer,
  ScheduleActionType,
  ScheduleReducerType,
} from 'lane-shared/domains/visitorManagement/reducers';

import Checkbox from 'components/form/Checkbox';
import { S, TimePicker, TimeIntervals } from 'design-system-web';

import MultiDatePickerButton from '../DatePickers/MultiDatePickerButton';

import styles from './ScheduleMultiDateTime.scss';

export type ScheduleMultiDateTimeHandle = {
  reset: () => void;
};

type Props = {
  className?: string;
  allDayPassEnabled: boolean;
  defaultIsAllDay: boolean;
  defaultStartDate: Date;
  defaultEndDate: Date;
  defaultDates: Date[];
  timeZone?: string;
  minDate?: Date;
  maxDate?: Date;
  onChange: (value: ScheduleReducerType) => void;
  isPreview?: boolean;
};

function ScheduleMultiDateTime(
  {
    className,
    allDayPassEnabled,
    defaultIsAllDay,
    defaultStartDate,
    defaultEndDate,
    defaultDates,
    timeZone,
    minDate,
    maxDate,
    onChange,
    isPreview = false,
  }: Props,
  ref: any
): React.ReactElement {
  const { t } = useTranslation();

  const reducerFunction = createScheduleReducer(
    {
      isAllDay: defaultIsAllDay,
      startDate: defaultStartDate,
      endDate: defaultEndDate,
      dates: defaultDates,
    },
    onChange,
    timeZone
  );

  const [state, dispatch] = useReducer(
    reducerFunction,
    {
      isAllDay: defaultIsAllDay,
      startDate: defaultStartDate,
      endDate: defaultEndDate,
      internalStartDate: removeTime(defaultStartDate, timeZone),
      internalEndDate: removeTime(defaultEndDate, timeZone),
      dates: defaultDates,
      startTime: defaultStartDate,
      endTime: defaultEndDate,
    }
  );

  useImperativeHandle(ref, () => {
    return {
      reset: () => {
        dispatch({
          type: ScheduleActionType.Reset,
        });
      },
    };
  });

  return (
    <div className={cx(styles.visitorScheduleMultiDateTime, className)}>
      {allDayPassEnabled && (
        <div className={styles.allDayCheckboxContainer}>
          <Checkbox
            selected={state.isAllDay}
            value={state.isAllDay}
            onChange={() => {
              dispatch({
                type: ScheduleActionType.ToggleAllDay,
              });
            }}
            className={styles.allDayCheckbox}
            labelStyles={{ fontSize: 16, paddingLeft: 7 }}
            text={t('web.content.feature.visitorManagement.form.allDay')}
            testId="allDayCheckbox"
            disabled={isPreview}
          />
        </div>
      )}
      <div className={styles.dateOptions}>
        <div className={styles.dateContainer}>
          <div className={styles.date}>
            <S data-test="vm-schedule-multi-date-time-select-dates">{t('web.content.feature.visitorManagement.form.selectDates')}</S>
            <MultiDatePickerButton
              value={state.dates}
              className={styles.datePicker}
              buttonClassName={styles.datePickerButton}
              onChange={() => {}}
              onSubmit={dates =>
                dispatch({
                  type: ScheduleActionType.SetDates,
                  value: { dates },
                })
              }
              hideLabel
              minDate={minDate}
              maxDate={maxDate}
              timeZone={timeZone}
              disabled={isPreview}
              data-test="vm-schedule-multi-date-time-multi-date-picker-button"
            />
          </div>
        </div>
        {(!allDayPassEnabled || !state.isAllDay) && (
          <div className={styles.timesContainer}>
            <div className={styles.times}>
              <div className={styles.group}>
                <S data-test="vm-schedule-multi-date-time-select-start-time">
                  {t(
                    'web.content.feature.visitorManagement.scheduler.form.startTime'
                  )}
                </S>
                <TimePicker
                  hideLabel
                  className={styles.timePicker}
                  buttonClassName={styles.timePickerButton}
                  value={state.startTime}
                  onChange={value => {
                    dispatch({
                      type: ScheduleActionType.SetStartTime,
                      value: {
                        date: new Date(value),
                      },
                    });
                  }}
                  showClear={false}
                  showIcon={false}
                  timeZone={timeZone}
                  unit={TimeIntervals.QUARTER_HOUR}
                  testId="startTime"
                  disabled={isPreview}
                />
              </div>
              <div className={styles.group}>
                <S data-test="vm-schedule-multi-date-time-select-end-time">
                  {t(
                    'web.content.feature.visitorManagement.scheduler.form.endTime'
                  )}
                </S>
                <TimePicker
                  hideLabel
                  className={styles.timePicker}
                  buttonClassName={styles.timePickerButton}
                  value={state.endTime}
                  onChange={value => {
                    dispatch({
                      type: ScheduleActionType.SetEndTime,
                      value: {
                        date: new Date(value),
                      },
                    });
                  }}
                  showClear={false}
                  showIcon={false}
                  timeZone={timeZone}
                  unit={TimeIntervals.QUARTER_HOUR}
                  testId="endTime"
                  disabled={isPreview}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const ScheduleMultiDateTimeForwarded = forwardRef<
  ScheduleMultiDateTimeHandle,
  Props
>(ScheduleMultiDateTime);

export default ScheduleMultiDateTimeForwarded;
